import { useState } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ListPageSkeleton } from "@/components/PageSkeleton";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { formatDate } from "@/lib/demo-data";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Phone,
  RefreshCw,
  StickyNote,
} from "lucide-react";
import { toast } from "sonner";

type Priority = "all" | "high_value" | "no_response" | "no_match";
type MarkStatus = "CONTACTED" | "REPLIED" | "CONVERTED" | "NOT_REACHED";

const TABS: { value: Priority; label: string }[] = [
  { value: "all", label: "Tous" },
  { value: "high_value", label: "High value" },
  { value: "no_response", label: "Pas de reponse" },
  { value: "no_match", label: "Pas matches" },
];

const STATUS_VARIANTS: Record<
  string,
  { label: string; variant: "default" | "secondary" | "destructive" | "outline" }
> = {
  NOT_CONTACTED: { label: "Non contacte", variant: "outline" },
  CONTACTED: { label: "Contacte", variant: "secondary" },
  REPLIED: { label: "Repondu", variant: "default" },
  CONVERTED: { label: "Converti", variant: "default" },
};

function maskPhone(phone: string | null | undefined): string {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 4) return phone;
  const head = digits.slice(0, 2);
  const tail = digits.slice(-2);
  return `${head} ** ** ** ${tail}`;
}

function formatBudget(min: number | null, max: number | null): string {
  if (min == null && max == null) return "—";
  if (min != null && max != null) return `${min.toLocaleString("fr-FR")} – ${max.toLocaleString("fr-FR")} €`;
  if (max != null) return `≤ ${max.toLocaleString("fr-FR")} €`;
  return `≥ ${(min as number).toLocaleString("fr-FR")} €`;
}

const PAGE_SIZE = 20;

// NB: opsCallQueue router not yet wired in apps/server/src/routers/admin/index.ts
// (other agent owns that file). We cast to any so the page typechecks; once
// wired, swap to trpc.admin.opsCallQueue.* directly.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const opsCallQueueClient = (trpc.admin as any).opsCallQueue;

interface QueueProject {
  id: string;
  title: string;
  category: string;
  city: string | null;
  department: string | null;
  budgetMin: number | null;
  budgetMax: number | null;
  status: string;
  isUrgent: boolean;
  unlocksCount: number;
  createdAt: string;
  opsContactStatus: string;
  opsNotes: string | null;
  lastContactAt: string | null;
  matchedCount: number;
  quotesCount: number;
  createdBy: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    phone: string | null;
    email: string;
  };
}

export default function OpsCallQueue() {
  const { user } = useAuth();
  const canAct = canWrite(user?.staffRole);

  const [tab, setTab] = useState<Priority>("all");
  const [page, setPage] = useState(1);
  const [budgetMinInput, setBudgetMinInput] = useState<string>("");
  const [budgetMin, setBudgetMin] = useState<number | undefined>(undefined);

  const [markDialog, setMarkDialog] = useState<{
    open: boolean;
    project: QueueProject | null;
  }>({ open: false, project: null });
  const [markStatus, setMarkStatus] = useState<MarkStatus>("CONTACTED");
  const [markNotes, setMarkNotes] = useState("");

  const queryArgs = {
    priority: tab,
    page,
    limit: PAGE_SIZE,
    ...(budgetMin != null ? { budgetMin } : {}),
  };

  const { data, isLoading, isError, refetch } =
    opsCallQueueClient.listCallQueue.useQuery(queryArgs);

  const markMutation = opsCallQueueClient.markCalled.useMutation({
    onSuccess: () => {
      toast.success("Appel enregistre");
      setMarkDialog({ open: false, project: null });
      setMarkNotes("");
      setMarkStatus("CONTACTED");
      refetch();
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (err: any) => toast.error(err?.message || "Erreur enregistrement"),
  });

  const items: QueueProject[] = data?.items ?? [];
  const total: number = data?.total ?? 0;
  const totalPages: number = data?.totalPages ?? 0;

  const handleTabChange = (next: string) => {
    setTab(next as Priority);
    setPage(1);
  };

  const handleApplyBudget = () => {
    const parsed = budgetMinInput.trim() === "" ? undefined : Number(budgetMinInput);
    if (parsed != null && Number.isNaN(parsed)) {
      toast.error("Budget min invalide");
      return;
    }
    setBudgetMin(parsed);
    setPage(1);
  };

  const openMarkDialog = (project: QueueProject) => {
    setMarkDialog({ open: true, project });
    setMarkStatus("CONTACTED");
    setMarkNotes("");
  };

  const submitMark = () => {
    if (!markDialog.project) return;
    markMutation.mutate({
      projectId: markDialog.project.id,
      status: markStatus,
      notes: markNotes.trim() || undefined,
    });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-semibold">File d'appels OPS</h1>
          <p className="text-muted-foreground">
            Outbound qualif call queue — particuliers à appeler
            {data ? ` (${total} entrées)` : ""}
          </p>
        </div>

        <Card>
          <CardHeader>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <CardTitle>Projets candidats</CardTitle>
              <div className="flex flex-wrap items-end gap-2">
                <div className="flex flex-col gap-1">
                  <Label htmlFor="budget-min" className="text-xs">
                    Budget min (€)
                  </Label>
                  <div className="flex gap-2">
                    <Input
                      id="budget-min"
                      type="number"
                      min={0}
                      step={1000}
                      placeholder="Ex 30000"
                      value={budgetMinInput}
                      onChange={(e) => setBudgetMinInput(e.target.value)}
                      className="h-9 w-[140px]"
                    />
                    <Button size="sm" variant="outline" onClick={handleApplyBudget}>
                      Filtrer
                    </Button>
                  </div>
                </div>
              </div>
            </div>
            <Tabs value={tab} onValueChange={handleTabChange} className="mt-4">
              <TabsList>
                {TABS.map((t) => (
                  <TabsTrigger key={t.value} value={t.value}>
                    {t.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <ListPageSkeleton />
            ) : isError ? (
              <div className="flex flex-col items-center justify-center min-h-[300px] gap-4">
                <AlertCircle className="h-12 w-12 text-destructive/40" aria-hidden="true" />
                <p className="text-muted-foreground font-medium">
                  Erreur de chargement de la file d'appels
                </p>
                <Button variant="outline" onClick={() => refetch()}>
                  <RefreshCw className="h-4 w-4 mr-2" aria-hidden="true" />
                  Reessayer
                </Button>
              </div>
            ) : items.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Projet</TableHead>
                    <TableHead>Particulier</TableHead>
                    <TableHead>Budget</TableHead>
                    <TableHead>Cree le</TableHead>
                    <TableHead>Statut OPS</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((p) => {
                    const statusCfg =
                      STATUS_VARIANTS[p.opsContactStatus] ?? {
                        label: p.opsContactStatus,
                        variant: "outline" as const,
                      };
                    const phone = p.createdBy.phone;
                    const callerName =
                      p.createdBy.firstName ||
                      p.createdBy.email.split("@")[0];
                    return (
                      <TableRow key={p.id} className="hover:bg-muted/50">
                        <TableCell className="max-w-[260px]">
                          <div className="font-medium truncate">{p.title}</div>
                          <div className="text-xs text-muted-foreground">
                            {p.category}
                            {p.city ? ` · ${p.city}` : ""}
                            {p.department ? ` (${p.department})` : ""}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {p.matchedCount} match · {p.quotesCount} devis · {p.unlocksCount} unlock
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{callerName}</div>
                          {phone ? (
                            <a
                              href={`tel:${phone}`}
                              className="text-xs text-primary underline"
                            >
                              {maskPhone(phone)}
                            </a>
                          ) : (
                            <span className="text-xs text-muted-foreground">
                              Pas de tel
                            </span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatBudget(p.budgetMin, p.budgetMax)}
                        </TableCell>
                        <TableCell className="text-sm">
                          <div>{formatDate(p.createdAt)}</div>
                          {p.lastContactAt && (
                            <div className="text-xs text-muted-foreground">
                              Dernier OPS : {formatDate(p.lastContactAt)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge variant={statusCfg.variant}>
                            {statusCfg.label}
                          </Badge>
                          {p.isUrgent && (
                            <Badge variant="destructive" className="ml-1 text-xs">
                              Urgent
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              asChild
                              disabled={!phone}
                            >
                              {phone ? (
                                <a href={`tel:${phone}`}>
                                  <Phone className="h-3 w-3 mr-1" />
                                  Appeler
                                </a>
                              ) : (
                                <span>
                                  <Phone className="h-3 w-3 mr-1" />
                                  Appeler
                                </span>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={!canAct}
                              onClick={() => openMarkDialog(p)}
                            >
                              <StickyNote className="h-3 w-3 mr-1" />
                              Marquer
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-12">
                <AlertCircle
                  className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3"
                  aria-hidden="true"
                />
                <p className="text-muted-foreground">
                  Aucun projet à appeler dans cette categorie
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Essayez un autre filtre ou revenez plus tard
                </p>
              </div>
            )}

            {data && total > PAGE_SIZE && (
              <div className="flex items-center justify-between border-t pt-4 mt-4">
                <div className="text-sm text-muted-foreground">
                  Affichage de {(page - 1) * PAGE_SIZE + 1} à{" "}
                  {Math.min(page * PAGE_SIZE, total)} sur {total}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1 || isLoading}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Precedent
                  </Button>
                  <div className="text-sm">
                    Page {page} sur {totalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={page >= totalPages || isLoading}
                  >
                    Suivant
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Mark dialog */}
        <Dialog
          open={markDialog.open}
          onOpenChange={(open) => {
            if (!open) {
              setMarkDialog({ open: false, project: null });
              setMarkNotes("");
              setMarkStatus("CONTACTED");
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Marquer l'appel</DialogTitle>
              <DialogDescription>
                {markDialog.project
                  ? `${markDialog.project.title} — ${markDialog.project.createdBy.firstName ?? markDialog.project.createdBy.email}`
                  : ""}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>Resultat de l'appel</Label>
                <Select
                  value={markStatus}
                  onValueChange={(v) => setMarkStatus(v as MarkStatus)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CONTACTED">Contacte</SelectItem>
                    <SelectItem value="REPLIED">A repondu</SelectItem>
                    <SelectItem value="CONVERTED">Converti</SelectItem>
                    <SelectItem value="NOT_REACHED">Pas joignable</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Notes</Label>
                <Textarea
                  value={markNotes}
                  onChange={(e) => setMarkNotes(e.target.value)}
                  placeholder="Resume de l'appel, objections, prochaine etape..."
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() =>
                  setMarkDialog({ open: false, project: null })
                }
              >
                Annuler
              </Button>
              <Button onClick={submitMark} disabled={markMutation.isPending}>
                {markMutation.isPending ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
